import React, { useState } from 'react';
import { PlusIcon, TrashIcon, ArrowLeftIcon, UserPlusIcon, MailIcon, BuildingIcon, GlobeIcon, SaveIcon, CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, BarChartIcon, CreditCardIcon } from 'lucide-react';
import { FileUpload } from '../FileUpload';
export function VendorOnboarding({
  onSave,
  onSendInvite,
  setCurrentView,
  entities = [],
  locale = 'en-US'
}) {
  const [onboardingMethod, setOnboardingMethod] = useState('direct');
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: '',
    country: '',
    taxId: '',
    website: '',
    contactName: '',
    email: '',
    phone: '',
    role: '',
    // Address fields
    street: '',
    city: '',
    state: '',
    postalCode: '',
    addressCountry: '',
    addressType: 'Physical',
    isPrimary: true,
    // Entity and site restrictions
    isGlobal: true,
    entities: [],
    sites: [],
    // ESG data
    esg: {
      score: '',
      environmental: '',
      social: '',
      governance: '',
      summary: ''
    },
    // Bank account fields
    bankName: '',
    accountName: '',
    accountNumber: '',
    routingNumber: '',
    swiftCode: '',
    iban: '',
    accountType: 'Checking'
  });
  const [inviteData, setInviteData] = useState({
    name: '',
    type: '',
    email: '',
    contactName: '',
    phone: '',
    role: '',
    message: 'We invite you to register as a vendor in our system. Please complete the onboarding process by following the link that will be sent to your email.'
  });
  const [errors, setErrors] = useState({});
  const [contacts, setContacts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [files, setFiles] = useState([]);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showBankAccountForm, setShowBankAccountForm] = useState(false);
  const [showESGForm, setShowESGForm] = useState(false);
  const [isESGEnabled] = useState(true); // In a real app, this would come from tenant settings
  const [hasESGEditPermission] = useState(true); // In a real app, this would come from user permissions
  const countries = ['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Japan', 'China', 'India'];
  const vendorTypes = ['Supplier', 'Manufacturer', 'Distributor', 'Service Provider', 'Consultant', 'Contractor', 'Other'];
  const addressTypes = ['Physical', 'Billing', 'Shipping', 'Mailing', 'Registered', 'Other'];
  const accountTypes = ['Checking', 'Savings', 'Current', 'Business', 'Corporate'];
  // Get available sites based on selected entities
  const getAvailableSites = () => {
    if (formData.isGlobal) return [];
    let availableSites = [];
    formData.entities.forEach(entityId => {
      const entity = entities.find(e => e.id === entityId);
      if (entity && entity.sites) {
        availableSites = [...availableSites, ...entity.sites];
      }
    });
    return availableSites;
  };
  // Handle entity selection
  const handleEntityChange = e => {
    const entityId = e.target.value;
    const isChecked = e.target.checked;
    if (isChecked) {
      setFormData({
        ...formData,
        entities: [...formData.entities, entityId]
      });
    } else {
      // Remove entity and any sites associated with this entity
      const entity = entities.find(e => e.id === entityId);
      const entitySiteIds = entity?.sites?.map(site => site.id) || [];
      setFormData({
        ...formData,
        entities: formData.entities.filter(id => id !== entityId),
        sites: formData.sites.filter(siteId => !entitySiteIds.includes(siteId))
      });
    }
  };
  // Handle site selection
  const handleSiteChange = e => {
    const siteId = e.target.value;
    const isChecked = e.target.checked;
    if (isChecked) {
      setFormData({
        ...formData,
        sites: [...formData.sites, siteId]
      });
    } else {
      setFormData({
        ...formData,
        sites: formData.sites.filter(id => id !== siteId)
      });
    }
  };
  // Handle availability type change
  const handleAvailabilityChange = isGlobal => {
    setFormData({
      ...formData,
      isGlobal,
      // Clear entity and site selections when switching to global
      entities: isGlobal ? [] : formData.entities,
      sites: isGlobal ? [] : formData.sites
    });
  };
  const handleInputChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    if (name.startsWith('esg.')) {
      const esgField = name.split('.')[1];
      setFormData({
        ...formData,
        esg: {
          ...formData.esg,
          [esgField]: value
        }
      });
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const handleInviteInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setInviteData({
      ...inviteData,
      [name]: value
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const addContact = () => {
    if (!formData.contactName || !formData.email) {
      setErrors({
        ...errors,
        contactName: !formData.contactName ? 'Contact name is required' : null,
        email: !formData.email ? 'Email is required' : null
      });
      return;
    }
    const newContact = {
      id: contacts.length + 1,
      name: formData.contactName,
      email: formData.email,
      phone: formData.phone || '',
      role: formData.role || ''
    };
    setContacts([...contacts, newContact]);
    setFormData({
      ...formData,
      contactName: '',
      email: '',
      phone: '',
      role: ''
    });
  };
  const removeContact = id => {
    setContacts(contacts.filter(contact => contact.id !== id));
  };
  const handleAddAddress = () => {
    if (!formData.street || !formData.city || !formData.addressCountry) {
      setErrors({
        ...errors,
        street: !formData.street ? 'Street is required' : null,
        city: !formData.city ? 'City is required' : null,
        addressCountry: !formData.addressCountry ? 'Country is required' : null
      });
      return;
    }
    const newAddress = {
      id: editingAddress ? editingAddress.id : addresses.length + 1,
      street: formData.street,
      city: formData.city,
      state: formData.state,
      postalCode: formData.postalCode,
      country: formData.addressCountry,
      addressType: formData.addressType,
      isPrimary: formData.isPrimary
    };
    let updatedAddresses = [];
    if (editingAddress) {
      // Update existing address
      updatedAddresses = addresses.map(addr => addr.id === editingAddress.id ? newAddress : addr);
    } else {
      // Add new address
      // If this is marked as primary, update all others to non-primary
      if (newAddress.isPrimary) {
        updatedAddresses = [...addresses.map(addr => ({
          ...addr,
          isPrimary: false
        })), newAddress];
      } else if (addresses.length === 0) {
        // First address should be primary by default
        updatedAddresses = [{
          ...newAddress,
          isPrimary: true
        }];
      } else {
        updatedAddresses = [...addresses, newAddress];
      }
    }
    setAddresses(updatedAddresses);
    setFormData({
      ...formData,
      street: '',
      city: '',
      state: '',
      postalCode: '',
      addressCountry: '',
      addressType: 'Physical',
      isPrimary: true
    });
    setShowAddressForm(false);
    setEditingAddress(null);
  };
  const handleEditAddress = address => {
    setEditingAddress(address);
    setFormData({
      ...formData,
      street: address.street,
      city: address.city,
      state: address.state || '',
      postalCode: address.postalCode || '',
      addressCountry: address.country,
      addressType: address.addressType || 'Physical',
      isPrimary: address.isPrimary || false
    });
    setShowAddressForm(true);
  };
  const updateAddress = () => {
    if (!formData.street || !formData.city || !formData.addressCountry) {
      setErrors({
        ...errors,
        street: !formData.street ? 'Street is required' : null,
        city: !formData.city ? 'City is required' : null,
        addressCountry: !formData.addressCountry ? 'Country is required' : null
      });
      return;
    }
    const updatedAddress = {
      id: editingAddress.id,
      street: formData.street,
      city: formData.city,
      state: formData.state || '',
      postalCode: formData.postalCode || '',
      country: formData.addressCountry,
      addressType: formData.addressType || 'Physical',
      isPrimary: formData.isPrimary
    };
    // Update existing address
    let updatedAddresses = addresses.map(address => address.id === editingAddress.id ? updatedAddress : address);
    // If this address is now primary, update all others to not be primary
    if (updatedAddress.isPrimary) {
      updatedAddresses = updatedAddresses.map(addr => addr.id === editingAddress.id ? addr : {
        ...addr,
        isPrimary: false
      });
    }
    setAddresses(updatedAddresses);
    // Clear form and exit edit mode
    setFormData({
      ...formData,
      street: '',
      city: '',
      state: '',
      postalCode: '',
      addressCountry: '',
      addressType: 'Physical',
      isPrimary: false
    });
    setEditingAddress(null);
    setShowAddressForm(false);
  };
  const removeAddress = id => {
    const addressToRemove = addresses.find(addr => addr.id === id);
    const wasRemovingPrimary = addressToRemove && addressToRemove.isPrimary;
    const filteredAddresses = addresses.filter(address => address.id !== id);
    // If we removed the primary address and there are other addresses left,
    // make the first one primary
    if (wasRemovingPrimary && filteredAddresses.length > 0) {
      filteredAddresses[0].isPrimary = true;
    }
    setAddresses(filteredAddresses);
  };
  // Bank Account functions
  const handleAddBankAccount = () => {
    if (!formData.bankName || !formData.accountName || !formData.accountNumber) {
      setErrors({
        ...errors,
        bankName: !formData.bankName ? 'Bank name is required' : null,
        accountName: !formData.accountName ? 'Account name is required' : null,
        accountNumber: !formData.accountNumber ? 'Account number is required' : null
      });
      return;
    }
    const newBankAccount = {
      id: editingBankAccount ? editingBankAccount.id : bankAccounts.length + 1,
      bankName: formData.bankName,
      accountName: formData.accountName,
      accountNumber: formData.accountNumber,
      routingNumber: formData.routingNumber,
      swiftCode: formData.swiftCode,
      iban: formData.iban,
      accountType: formData.accountType,
      isPrimary: formData.isPrimary,
      country: formData.addressCountry || formData.country
    };
    let updatedBankAccounts = [];
    if (editingBankAccount) {
      // Update existing bank account
      updatedBankAccounts = bankAccounts.map(account => account.id === editingBankAccount.id ? newBankAccount : account);
    } else {
      // Add new bank account
      // If this is marked as primary, update all others to non-primary
      if (newBankAccount.isPrimary) {
        updatedBankAccounts = [...bankAccounts.map(account => ({
          ...account,
          isPrimary: false
        })), newBankAccount];
      } else if (bankAccounts.length === 0) {
        // First bank account should be primary by default
        updatedBankAccounts = [{
          ...newBankAccount,
          isPrimary: true
        }];
      } else {
        updatedBankAccounts = [...bankAccounts, newBankAccount];
      }
    }
    setBankAccounts(updatedBankAccounts);
    setFormData({
      ...formData,
      bankName: '',
      accountName: '',
      accountNumber: '',
      routingNumber: '',
      swiftCode: '',
      iban: '',
      accountType: 'Checking',
      isPrimary: false
    });
    setShowBankAccountForm(false);
    setEditingBankAccount(null);
  };
  const handleEditBankAccount = account => {
    setEditingBankAccount(account);
    setFormData({
      ...formData,
      bankName: account.bankName,
      accountName: account.accountName,
      accountNumber: account.accountNumber,
      routingNumber: account.routingNumber || '',
      swiftCode: account.swiftCode || '',
      iban: account.iban || '',
      accountType: account.accountType || 'Checking',
      isPrimary: account.isPrimary || false
    });
    setShowBankAccountForm(true);
  };
  const removeBankAccount = id => {
    const accountToRemove = bankAccounts.find(account => account.id === id);
    const wasRemovingPrimary = accountToRemove && accountToRemove.isPrimary;
    const filteredAccounts = bankAccounts.filter(account => account.id !== id);
    // If we removed the primary account and there are other accounts left,
    // make the first one primary
    if (wasRemovingPrimary && filteredAccounts.length > 0) {
      filteredAccounts[0].isPrimary = true;
    }
    setBankAccounts(filteredAccounts);
  };
  const cancelBankAccountEdit = () => {
    setFormData({
      ...formData,
      bankName: '',
      accountName: '',
      accountNumber: '',
      routingNumber: '',
      swiftCode: '',
      iban: '',
      accountType: 'Checking',
      isPrimary: false
    });
    setEditingBankAccount(null);
    setShowBankAccountForm(false);
  };
  const handleFileUpload = newFiles => {
    setFiles([...files, ...newFiles]);
  };
  const removeFile = index => {
    setFiles(files.filter((_, i) => i !== index));
  };
  const validateForm = () => {
    const newErrors = {};
    if (onboardingMethod === 'direct') {
      // Validate required fields for direct onboarding
      if (!formData.name) newErrors.name = 'Vendor name is required';
      if (!formData.type) newErrors.type = 'Vendor type is required';
      if (!formData.country) newErrors.country = 'Country is required';
      if (contacts.length === 0) newErrors.contacts = 'At least one contact is required';
      if (addresses.length === 0) newErrors.addresses = 'At least one address is required';
      // Validate entity selections if not global
      if (!formData.isGlobal && formData.entities.length === 0) {
        newErrors.entities = 'At least one entity must be selected';
      }
      // Validate ESG data if provided
      if (formData.esg && formData.esg.score) {
        const score = parseInt(formData.esg.score);
        if (isNaN(score) || score < 0 || score > 100) {
          newErrors['esg.score'] = 'Score must be a number between 0 and 100';
        }
        if (formData.esg.environmental) {
          const envScore = parseInt(formData.esg.environmental);
          if (isNaN(envScore) || envScore < 0 || envScore > 100) {
            newErrors['esg.environmental'] = 'Score must be a number between 0 and 100';
          }
        }
        if (formData.esg.social) {
          const socialScore = parseInt(formData.esg.social);
          if (isNaN(socialScore) || socialScore < 0 || socialScore > 100) {
            newErrors['esg.social'] = 'Score must be a number between 0 and 100';
          }
        }
        if (formData.esg.governance) {
          const govScore = parseInt(formData.esg.governance);
          if (isNaN(govScore) || govScore < 0 || govScore > 100) {
            newErrors['esg.governance'] = 'Score must be a number between 0 and 100';
          }
        }
      }
    } else {
      // Validate required fields for invite
      if (!inviteData.name) newErrors.name = 'Vendor name is required';
      if (!inviteData.email) newErrors.email = 'Email is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    if (onboardingMethod === 'direct') {
      // Prepare data for saving
      const vendorData = {
        ...formData,
        status: 'Active',
        complianceStatus: files.length > 0 ? 'Pending Review' : 'Incomplete',
        contacts,
        addresses,
        bankAccounts,
        documents: files.map((file, index) => ({
          id: index + 1,
          name: file.name,
          type: 'Supporting Document',
          uploadDate: new Date().toISOString().split('T')[0],
          expiryDate: null,
          status: 'Valid'
        })),
        esg: formData.esg.score ? {
          score: parseInt(formData.esg.score),
          environmental: formData.esg.environmental ? parseInt(formData.esg.environmental) : null,
          social: formData.esg.social ? parseInt(formData.esg.social) : null,
          governance: formData.esg.governance ? parseInt(formData.esg.governance) : null,
          summary: formData.esg.summary || '',
          lastUpdated: new Date().toISOString().split('T')[0]
        } : null
      };
      // Call the save handler from parent
      onSave(vendorData);
    } else {
      // Send invitation with availability settings
      const inviteWithAvailability = {
        ...inviteData,
        isGlobal: formData.isGlobal,
        entities: formData.entities,
        sites: formData.sites
      };
      onSendInvite(inviteWithAvailability);
    }
  };
  const cancelAddressEdit = () => {
    setFormData({
      ...formData,
      street: '',
      city: '',
      state: '',
      postalCode: '',
      addressCountry: '',
      addressType: 'Physical',
      isPrimary: false
    });
    setEditingAddress(null);
    setShowAddressForm(false);
  };
  return <div data-id="element-20961">
      <div className="mb-8 flex items-center" data-id="element-20962">
        <button onClick={() => setCurrentView('vendors')} className="mr-4 text-gray-400 hover:text-gray-500" data-id="element-20963">
          <ArrowLeftIcon className="h-5 w-5" data-id="element-20964" />
        </button>
        <div data-id="element-20965">
          <h3 className="text-xl font-semibold text-gray-900" data-id="element-20966">
            Add New Vendor
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-20967">
            Create a new vendor record or invite a vendor to onboard themselves
          </p>
        </div>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-20968">
        <div className="px-4 py-5 sm:px-6" data-id="element-20969">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20970">
            Onboarding Method
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-20971">
            Choose how you want to onboard this vendor
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-20972">
          <div className="flex flex-col sm:flex-row sm:space-x-4" data-id="element-20973">
            <div className={`flex-1 p-4 border rounded-lg mb-4 sm:mb-0 cursor-pointer ${onboardingMethod === 'direct' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`} onClick={() => setOnboardingMethod('direct')} data-id="element-20974">
              <div className="flex items-center" data-id="element-20975">
                <div className="flex-shrink-0" data-id="element-20976">
                  <div className={`h-5 w-5 rounded-full border ${onboardingMethod === 'direct' ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'} flex items-center justify-center`} data-id="element-20977">
                    {onboardingMethod === 'direct' && <CheckIcon className="h-3 w-3 text-white" data-id="element-20978" />}
                  </div>
                </div>
                <div className="ml-3" data-id="element-20979">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-20980">
                    Direct Entry
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-20981">
                    Enter vendor information directly into the system
                  </p>
                </div>
              </div>
            </div>
            <div className={`flex-1 p-4 border rounded-lg cursor-pointer ${onboardingMethod === 'invite' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`} onClick={() => setOnboardingMethod('invite')} data-id="element-20982">
              <div className="flex items-center" data-id="element-20983">
                <div className="flex-shrink-0" data-id="element-20984">
                  <div className={`h-5 w-5 rounded-full border ${onboardingMethod === 'invite' ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'} flex items-center justify-center`} data-id="element-20985">
                    {onboardingMethod === 'invite' && <CheckIcon className="h-3 w-3 text-white" data-id="element-20986" />}
                  </div>
                </div>
                <div className="ml-3" data-id="element-20987">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-20988">
                    Send Invitation
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-20989">
                    Send an invitation to the vendor to complete their own
                    profile
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {onboardingMethod === 'direct' ? <>
          {/* Basic Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-20990">
            <div className="px-4 py-5 sm:px-6" data-id="element-20991">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20992">
                Basic Information
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-20993">
                Enter vendor details and basic information
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-20994">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-20995">
                <div className="sm:col-span-3" data-id="element-20996">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-20997">
                    Vendor Name <span className="text-red-500" data-id="element-20998">*</span>
                  </label>
                  <div className="mt-1" data-id="element-20999">
                    <input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.name ? 'border-red-300' : ''}`} data-id="element-21000" />
                    {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-21001">{errors.name}</p>}
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-21002">
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700" data-id="element-21003">
                    Vendor Type <span className="text-red-500" data-id="element-21004">*</span>
                  </label>
                  <div className="mt-1" data-id="element-21005">
                    <select id="type" name="type" value={formData.type} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.type ? 'border-red-300' : ''}`} data-id="element-21006">
                      <option value="" data-id="element-21007">Select vendor type</option>
                      {vendorTypes.map(type => <option key={type} value={type} data-id="element-21008">
                          {type}
                        </option>)}
                    </select>
                    {errors.type && <p className="mt-1 text-sm text-red-600" data-id="element-21009">{errors.type}</p>}
                  </div>
                </div>
                <div className="sm:col-span-6" data-id="element-21010">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-21011">
                    Description
                  </label>
                  <div className="mt-1" data-id="element-21012">
                    <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-21013" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-21014">
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700" data-id="element-21015">
                    Country <span className="text-red-500" data-id="element-21016">*</span>
                  </label>
                  <div className="mt-1" data-id="element-21017">
                    <select id="country" name="country" value={formData.country} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.country ? 'border-red-300' : ''}`} data-id="element-21018">
                      <option value="" data-id="element-21019">Select country</option>
                      {countries.map(country => <option key={country} value={country} data-id="element-21020">
                          {country}
                        </option>)}
                    </select>
                    {errors.country && <p className="mt-1 text-sm text-red-600" data-id="element-21021">
                        {errors.country}
                      </p>}
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-21022">
                  <label htmlFor="taxId" className="block text-sm font-medium text-gray-700" data-id="element-21023">
                    Tax ID / Business Number
                  </label>
                  <div className="mt-1" data-id="element-21024">
                    <input type="text" name="taxId" id="taxId" value={formData.taxId} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-21025" />
                  </div>
                </div>
                <div className="sm:col-span-6" data-id="element-21026">
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700" data-id="element-21027">
                    Website
                  </label>
                  <div className="mt-1" data-id="element-21028">
                    <input type="url" name="website" id="website" value={formData.website} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-21029" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* ESG Information */}
          {isESGEnabled && hasESGEditPermission && <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21030">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-21031">
                <div data-id="element-21032">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21033">
                    ESG Information
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-21034">
                    Environmental, Social, and Governance data
                  </p>
                </div>
                <button type="button" onClick={() => setShowESGForm(!showESGForm)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21035">
                  {showESGForm ? <>
                      <ChevronUpIcon className="h-4 w-4 mr-1" data-id="element-21036" />
                      Hide ESG Form
                    </> : <>
                      <ChevronDownIcon className="h-4 w-4 mr-1" data-id="element-21037" />
                      Add ESG Data
                    </>}
                </button>
              </div>
              {showESGForm && <div className="border-t border-gray-200 px-4 py-5" data-id="element-21038">
                  <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-21039">
                    <div className="sm:col-span-2" data-id="element-21040">
                      <label htmlFor="esg.score" className="block text-sm font-medium text-gray-700" data-id="element-21041">
                        Overall ESG Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-21042">
                        <input type="number" name="esg.score" id="esg.score" min="0" max="100" value={formData.esg.score} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.score'] ? 'border-red-300' : ''}`} data-id="element-21043" />
                        {errors['esg.score'] && <p className="mt-1 text-sm text-red-600" data-id="element-21044">
                            {errors['esg.score']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-2" data-id="element-21045">
                      <label htmlFor="esg.environmental" className="block text-sm font-medium text-gray-700" data-id="element-21046">
                        Environmental Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-21047">
                        <input type="number" name="esg.environmental" id="esg.environmental" min="0" max="100" value={formData.esg.environmental} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.environmental'] ? 'border-red-300' : ''}`} data-id="element-21048" />
                        {errors['esg.environmental'] && <p className="mt-1 text-sm text-red-600" data-id="element-21049">
                            {errors['esg.environmental']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-2" data-id="element-21050">
                      <label htmlFor="esg.social" className="block text-sm font-medium text-gray-700" data-id="element-21051">
                        Social Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-21052">
                        <input type="number" name="esg.social" id="esg.social" min="0" max="100" value={formData.esg.social} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.social'] ? 'border-red-300' : ''}`} data-id="element-21053" />
                        {errors['esg.social'] && <p className="mt-1 text-sm text-red-600" data-id="element-21054">
                            {errors['esg.social']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-3" data-id="element-21055">
                      <label htmlFor="esg.governance" className="block text-sm font-medium text-gray-700" data-id="element-21056">
                        Governance Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-21057">
                        <input type="number" name="esg.governance" id="esg.governance" min="0" max="100" value={formData.esg.governance} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.governance'] ? 'border-red-300' : ''}`} data-id="element-21058" />
                        {errors['esg.governance'] && <p className="mt-1 text-sm text-red-600" data-id="element-21059">
                            {errors['esg.governance']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-6" data-id="element-21060">
                      <label htmlFor="esg.summary" className="block text-sm font-medium text-gray-700" data-id="element-21061">
                        ESG Summary
                      </label>
                      <div className="mt-1" data-id="element-21062">
                        <textarea id="esg.summary" name="esg.summary" rows={3} value={formData.esg.summary} onChange={handleInputChange} placeholder="Brief summary of vendor's ESG initiatives and performance" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-21063" />
                      </div>
                    </div>
                  </div>
                </div>}
            </div>}
          {/* Contact Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21064">
            <div className="px-4 py-5 sm:px-6" data-id="element-21065">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21066">
                Contact Information
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-21067">
                Add primary and additional contacts for this vendor
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-21068">
              <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden" data-id="element-21069">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-21070">
                  <h4 className="text-base font-medium text-gray-900" data-id="element-21071">
                    Add Contact
                  </h4>
                </div>
                <div className="p-4" data-id="element-21072">
                  <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2" data-id="element-21073">
                    <div data-id="element-21074">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21075">
                        Contact Name <span className="text-red-500" data-id="element-21076">*</span>
                      </label>
                      <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.contactName ? 'border-red-300' : ''}`} value={formData.contactName} name="contactName" onChange={handleInputChange} placeholder="Full name" data-id="element-21077" />
                      {errors.contactName && <p className="mt-1 text-sm text-red-600" data-id="element-21078">
                          {errors.contactName}
                        </p>}
                    </div>
                    <div data-id="element-21079">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21080">
                        Email <span className="text-red-500" data-id="element-21081">*</span>
                      </label>
                      <input type="email" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.email ? 'border-red-300' : ''}`} value={formData.email} name="email" onChange={handleInputChange} placeholder="Email address" data-id="element-21082" />
                      {errors.email && <p className="mt-1 text-sm text-red-600" data-id="element-21083">
                          {errors.email}
                        </p>}
                    </div>
                    <div data-id="element-21084">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21085">
                        Phone
                      </label>
                      <input type="tel" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.phone} name="phone" onChange={handleInputChange} placeholder="Phone number" data-id="element-21086" />
                    </div>
                    <div data-id="element-21087">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21088">
                        Role
                      </label>
                      <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.role} name="role" onChange={handleInputChange} placeholder="Job title or role" data-id="element-21089" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-21090">
                    <button type="button" onClick={addContact} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21091">
                      <PlusIcon className="h-4 w-4 mr-1" data-id="element-21092" />
                      Add Contact
                    </button>
                  </div>
                </div>
              </div>
              {errors.contacts && <div className="mb-4" data-id="element-21093">
                  <p className="text-sm text-red-600" data-id="element-21094">{errors.contacts}</p>
                </div>}
              {contacts.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-21095">
                  {contacts.map(contact => <li key={contact.id} className="py-4 flex items-center" data-id="element-21096">
                      <div className="flex-shrink-0" data-id="element-21097">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500" data-id="element-21098">
                          <UserPlusIcon className="h-5 w-5" data-id="element-21099" />
                        </div>
                      </div>
                      <div className="ml-3 flex-1" data-id="element-21100">
                        <p className="text-sm font-medium text-gray-900" data-id="element-21101">
                          {contact.name}
                        </p>
                        <div className="flex items-center text-sm text-gray-500" data-id="element-21102">
                          <MailIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-21103" />
                          {contact.email}
                        </div>
                        {contact.phone && <p className="mt-1 text-sm text-gray-500" data-id="element-21104">
                            {contact.phone}
                          </p>}
                        {contact.role && <p className="mt-1 text-sm text-gray-500" data-id="element-21105">
                            {contact.role}
                          </p>}
                      </div>
                      <div className="ml-4" data-id="element-21106">
                        <button type="button" onClick={() => removeContact(contact.id)} className="text-red-600 hover:text-red-900" data-id="element-21107">
                          <TrashIcon className="h-5 w-5" data-id="element-21108" />
                        </button>
                      </div>
                    </li>)}
                </ul> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-21109">
                  No contacts added yet. Add at least one contact.
                </div>}
            </div>
          </div>
          {/* Address Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21110">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-21111">
              <div data-id="element-21112">
                <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21113">
                  Address Information
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-21114">
                  Add addresses for this vendor
                </p>
              </div>
              <button type="button" onClick={() => setShowAddressForm(!showAddressForm)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21115">
                {showAddressForm ? <>
                    <ChevronUpIcon className="h-4 w-4 mr-1" data-id="element-21116" />
                    Hide Address Form
                  </> : <>
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-21117" />
                    Add Address
                  </>}
              </button>
            </div>
            {showAddressForm && <div className="px-4 py-5 border-t border-gray-200" data-id="element-21118">
                <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden" data-id="element-21119">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-21120">
                    <h4 className="text-base font-medium text-gray-900" data-id="element-21121">
                      {editingAddress ? 'Edit Address' : 'Add New Address'}
                    </h4>
                  </div>
                  <div className="p-4" data-id="element-21122">
                    <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2" data-id="element-21123">
                      <div data-id="element-21124">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21125">
                          Street Address <span className="text-red-500" data-id="element-21126">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.street ? 'border-red-300' : ''}`} value={formData.street} name="street" onChange={handleInputChange} placeholder="Street address" data-id="element-21127" />
                        {errors.street && <p className="mt-1 text-sm text-red-600" data-id="element-21128">
                            {errors.street}
                          </p>}
                      </div>
                      <div data-id="element-21129">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21130">
                          City <span className="text-red-500" data-id="element-21131">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.city ? 'border-red-300' : ''}`} value={formData.city} name="city" onChange={handleInputChange} placeholder="City" data-id="element-21132" />
                        {errors.city && <p className="mt-1 text-sm text-red-600" data-id="element-21133">
                            {errors.city}
                          </p>}
                      </div>
                      <div data-id="element-21134">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21135">
                          State/Province
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.state} name="state" onChange={handleInputChange} placeholder="State or province" data-id="element-21136" />
                      </div>
                      <div data-id="element-21137">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21138">
                          Postal Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.postalCode} name="postalCode" onChange={handleInputChange} placeholder="Postal code" data-id="element-21139" />
                      </div>
                      <div data-id="element-21140">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21141">
                          Country <span className="text-red-500" data-id="element-21142">*</span>
                        </label>
                        <select className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.addressCountry ? 'border-red-300' : ''}`} value={formData.addressCountry} name="addressCountry" onChange={handleInputChange} data-id="element-21143">
                          <option value="" data-id="element-21144">Select country</option>
                          {countries.map(country => <option key={country} value={country} data-id="element-21145">
                              {country}
                            </option>)}
                        </select>
                        {errors.addressCountry && <p className="mt-1 text-sm text-red-600" data-id="element-21146">
                            {errors.addressCountry}
                          </p>}
                      </div>
                      <div data-id="element-21147">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21148">
                          Address Type
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.addressType} name="addressType" onChange={handleInputChange} data-id="element-21149">
                          {addressTypes.map(type => <option key={type} value={type} data-id="element-21150">
                              {type}
                            </option>)}
                        </select>
                      </div>
                      <div className="flex items-center mt-4" data-id="element-21151">
                        <input id="isPrimary" name="isPrimary" type="checkbox" checked={formData.isPrimary} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-21152" />
                        <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-700" data-id="element-21153">
                          Primary Address
                        </label>
                      </div>
                    </div>
                    <div className="mt-4 flex space-x-3" data-id="element-21154">
                      <button type="button" onClick={handleAddAddress} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21155">
                        {editingAddress ? 'Update Address' : 'Add Address'}
                      </button>
                      <button type="button" onClick={() => {
                  setShowAddressForm(false);
                  setEditingAddress(null);
                }} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21156">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>}
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-21157">
              {errors.addresses && <div className="mb-4" data-id="element-21158">
                  <p className="text-sm text-red-600" data-id="element-21159">{errors.addresses}</p>
                </div>}
              {addresses.length > 0 ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-21160">
                  {addresses.map(address => <div key={address.id} className={`relative rounded-lg border ${address.isPrimary ? 'border-indigo-500' : 'border-gray-300'} bg-white px-4 py-3 shadow-sm flex flex-col space-y-2`} data-id="element-21161">
                      {address.isPrimary && <span className="absolute top-0 right-0 -mt-2 -mr-2 px-2 py-1 text-xs font-bold rounded-full bg-indigo-100 text-indigo-800 flex items-center" data-id="element-21162">
                          <CheckIcon className="h-3 w-3 mr-1" data-id="element-21163" />
                          Primary
                        </span>}
                      <div className="flex items-center space-x-3" data-id="element-21164">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-21165">
                          <BuildingIcon className="h-5 w-5 text-gray-500" data-id="element-21166" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-21167">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-21168">
                            {address.addressType}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-21169">
                        <p data-id="element-21170">{address.street}</p>
                        <p data-id="element-21171">
                          {address.city}
                          {address.state && `, ${address.state}`}{' '}
                          {address.postalCode}
                        </p>
                        <p data-id="element-21172">{address.country}</p>
                      </div>
                      <div className="flex justify-end space-x-2 mt-2" data-id="element-21173">
                        <button type="button" onClick={() => handleEditAddress(address)} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-21174">
                          Edit
                        </button>
                        <button type="button" onClick={() => removeAddress(address.id)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-21175">
                          Remove
                        </button>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-21176">
                  No addresses added yet. Add at least one address.
                </div>}
            </div>
          </div>
          {/* Bank Account Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21177">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-21178">
              <div data-id="element-21179">
                <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21180">
                  Bank Account Information
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-21181">
                  Add payment bank accounts for this vendor
                </p>
              </div>
              <button type="button" onClick={() => setShowBankAccountForm(!showBankAccountForm)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21182">
                {showBankAccountForm ? <>
                    <ChevronUpIcon className="h-4 w-4 mr-1" data-id="element-21183" />
                    Hide Bank Account Form
                  </> : <>
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-21184" />
                    Add Bank Account
                  </>}
              </button>
            </div>
            {showBankAccountForm && <div className="px-4 py-5 border-t border-gray-200" data-id="element-21185">
                <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden" data-id="element-21186">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-21187">
                    <h4 className="text-base font-medium text-gray-900" data-id="element-21188">
                      {editingBankAccount ? 'Edit Bank Account' : 'Add New Bank Account'}
                    </h4>
                  </div>
                  <div className="p-4" data-id="element-21189">
                    <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2" data-id="element-21190">
                      <div data-id="element-21191">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21192">
                          Bank Name <span className="text-red-500" data-id="element-21193">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.bankName ? 'border-red-300' : ''}`} value={formData.bankName} name="bankName" onChange={handleInputChange} placeholder="Bank name" data-id="element-21194" />
                        {errors.bankName && <p className="mt-1 text-sm text-red-600" data-id="element-21195">
                            {errors.bankName}
                          </p>}
                      </div>
                      <div data-id="element-21196">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21197">
                          Account Name <span className="text-red-500" data-id="element-21198">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.accountName ? 'border-red-300' : ''}`} value={formData.accountName} name="accountName" onChange={handleInputChange} placeholder="Account holder name" data-id="element-21199" />
                        {errors.accountName && <p className="mt-1 text-sm text-red-600" data-id="element-21200">
                            {errors.accountName}
                          </p>}
                      </div>
                      <div data-id="element-21201">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21202">
                          Account Number <span className="text-red-500" data-id="element-21203">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.accountNumber ? 'border-red-300' : ''}`} value={formData.accountNumber} name="accountNumber" onChange={handleInputChange} placeholder="Account number" data-id="element-21204" />
                        {errors.accountNumber && <p className="mt-1 text-sm text-red-600" data-id="element-21205">
                            {errors.accountNumber}
                          </p>}
                      </div>
                      <div data-id="element-21206">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21207">
                          Routing/BSB/Sort Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.routingNumber} name="routingNumber" onChange={handleInputChange} placeholder="Routing/BSB/Sort code" data-id="element-21208" />
                      </div>
                      <div data-id="element-21209">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21210">
                          SWIFT/BIC Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.swiftCode} name="swiftCode" onChange={handleInputChange} placeholder="SWIFT/BIC code" data-id="element-21211" />
                      </div>
                      <div data-id="element-21212">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21213">
                          IBAN
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.iban} name="iban" onChange={handleInputChange} placeholder="IBAN (if applicable)" data-id="element-21214" />
                      </div>
                      <div data-id="element-21215">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21216">
                          Account Type
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.accountType} name="accountType" onChange={handleInputChange} data-id="element-21217">
                          {accountTypes.map(type => <option key={type} value={type} data-id="element-21218">
                              {type}
                            </option>)}
                        </select>
                      </div>
                      <div className="flex items-center mt-4" data-id="element-21219">
                        <input id="isPrimary" name="isPrimary" type="checkbox" checked={formData.isPrimary} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-21220" />
                        <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-700" data-id="element-21221">
                          Primary Bank Account
                        </label>
                      </div>
                    </div>
                    <div className="mt-4 flex space-x-3" data-id="element-21222">
                      <button type="button" onClick={handleAddBankAccount} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21223">
                        {editingBankAccount ? 'Update Bank Account' : 'Add Bank Account'}
                      </button>
                      <button type="button" onClick={cancelBankAccountEdit} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21224">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>}
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-21225">
              {bankAccounts.length > 0 ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-21226">
                  {bankAccounts.map(account => <div key={account.id} className={`relative rounded-lg border ${account.isPrimary ? 'border-indigo-500' : 'border-gray-300'} bg-white px-4 py-3 shadow-sm flex flex-col space-y-2`} data-id="element-21227">
                      {account.isPrimary && <span className="absolute top-0 right-0 -mt-2 -mr-2 px-2 py-1 text-xs font-bold rounded-full bg-indigo-100 text-indigo-800 flex items-center" data-id="element-21228">
                          <CheckIcon className="h-3 w-3 mr-1" data-id="element-21229" />
                          Primary
                        </span>}
                      <div className="flex items-center space-x-3" data-id="element-21230">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-21231">
                          <div className="h-5 w-5 text-gray-500" data-id="element-21232" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-21233">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-21234">
                            {account.bankName}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-21235">
                        <p data-id="element-21236">Account Name: {account.accountName}</p>
                        <p data-id="element-21237">
                          Account Number: •••• {account.accountNumber.slice(-4)}
                        </p>
                        <p data-id="element-21238">Account Type: {account.accountType}</p>
                        {account.routingNumber && <p data-id="element-21239">Routing: {account.routingNumber}</p>}
                      </div>
                      <div className="flex justify-end space-x-2 mt-2" data-id="element-21240">
                        <button type="button" onClick={() => handleEditBankAccount(account)} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-21241">
                          Edit
                        </button>
                        <button type="button" onClick={() => removeBankAccount(account.id)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-21242">
                          Remove
                        </button>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-21243">
                  No bank accounts added yet. Add at least one bank account for
                  payment processing.
                </div>}
            </div>
          </div>
          {/* Entity Access */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21244">
            <div className="px-4 py-5 sm:px-6" data-id="element-21245">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21246">
                Entity Access
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-21247">
                Define which entities can use this vendor
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-21248">
              <div className="flex items-center mb-4" data-id="element-21249">
                <input id="isGlobal" name="isGlobal" type="checkbox" checked={formData.isGlobal} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-21250" />
                <label htmlFor="isGlobal" className="ml-2 block text-sm text-gray-700" data-id="element-21251">
                  Global Vendor (available to all entities)
                </label>
              </div>
              {!formData.isGlobal && <div className="mt-4" data-id="element-21252">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-21253">
                    Select Entities
                  </label>
                  <select multiple className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border h-32" value={formData.entities} onChange={e => {
              const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
              setFormData({
                ...formData,
                entities: selectedOptions
              });
            }} data-id="element-21254">
                    {entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-21255">
                        {entity.name}
                      </option>)}
                  </select>
                  <p className="mt-1 text-xs text-gray-500" data-id="element-21256">
                    Hold Ctrl/Cmd to select multiple entities
                  </p>
                </div>}
            </div>
          </div>
          {/* Documents */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21257">
            <div className="px-4 py-5 sm:px-6" data-id="element-21258">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21259">
                Documents
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-21260">
                Upload supporting documents
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-21261">
              <FileUpload onFileUpload={handleFileUpload} data-id="element-21262" />
              {files.length > 0 && <div className="mt-4" data-id="element-21263">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-21264">
                    Files to Upload
                  </h4>
                  <ul className="border rounded-md divide-y divide-gray-200" data-id="element-21265">
                    {files.map((file, index) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-21266">
                        <div className="w-0 flex-1 flex items-center" data-id="element-21267">
                          <svg className="flex-shrink-0 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-21268">
                            <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" data-id="element-21269" />
                          </svg>
                          <span className="ml-2 flex-1 w-0 truncate" data-id="element-21270">
                            {file.name}
                          </span>
                        </div>
                        <div className="ml-4 flex-shrink-0" data-id="element-21271">
                          <button type="button" onClick={() => removeFile(index)} className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-21272">
                            Remove
                          </button>
                        </div>
                      </li>)}
                  </ul>
                </div>}
            </div>
          </div>
          {/* Submit Button */}
          <div className="flex justify-end" data-id="element-21273">
            <button type="button" onClick={() => setCurrentView('vendors')} className="mr-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21274">
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21275">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-21276" />
              Save Vendor
            </button>
          </div>
        </> :
    // Send Invitation Form
    <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21277">
          <div className="px-4 py-5 sm:px-6" data-id="element-21278">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21279">
              Send Vendor Invitation
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-21280">
              Invite a vendor to complete their own onboarding
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5" data-id="element-21281">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-21282">
              <div className="sm:col-span-3" data-id="element-21283">
                <label htmlFor="inviteName" className="block text-sm font-medium text-gray-700" data-id="element-21284">
                  Vendor Name <span className="text-red-500" data-id="element-21285">*</span>
                </label>
                <div className="mt-1" data-id="element-21286">
                  <input type="text" name="name" id="inviteName" value={inviteData.name} onChange={handleInviteInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.inviteName ? 'border-red-300' : ''}`} data-id="element-21287" />
                  {errors.inviteName && <p className="mt-1 text-sm text-red-600" data-id="element-21288">
                      {errors.inviteName}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-21289">
                <label htmlFor="inviteType" className="block text-sm font-medium text-gray-700" data-id="element-21290">
                  Vendor Type
                </label>
                <div className="mt-1" data-id="element-21291">
                  <select id="inviteType" name="type" value={inviteData.type} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-21292">
                    <option value="" data-id="element-21293">Select vendor type</option>
                    {vendorTypes.map(type => <option key={type} value={type} data-id="element-21294">
                        {type}
                      </option>)}
                  </select>
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-21295">
                <label htmlFor="inviteContactName" className="block text-sm font-medium text-gray-700" data-id="element-21296">
                  Contact Name <span className="text-red-500" data-id="element-21297">*</span>
                </label>
                <div className="mt-1" data-id="element-21298">
                  <input type="text" name="contactName" id="inviteContactName" value={inviteData.contactName} onChange={handleInviteInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.inviteContactName ? 'border-red-300' : ''}`} data-id="element-21299" />
                  {errors.inviteContactName && <p className="mt-1 text-sm text-red-600" data-id="element-21300">
                      {errors.inviteContactName}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-21301">
                <label htmlFor="inviteEmail" className="block text-sm font-medium text-gray-700" data-id="element-21302">
                  Email <span className="text-red-500" data-id="element-21303">*</span>
                </label>
                <div className="mt-1" data-id="element-21304">
                  <input type="email" name="email" id="inviteEmail" value={inviteData.email} onChange={handleInviteInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.inviteEmail ? 'border-red-300' : ''}`} data-id="element-21305" />
                  {errors.inviteEmail && <p className="mt-1 text-sm text-red-600" data-id="element-21306">
                      {errors.inviteEmail}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-21307">
                <label htmlFor="invitePhone" className="block text-sm font-medium text-gray-700" data-id="element-21308">
                  Phone
                </label>
                <div className="mt-1" data-id="element-21309">
                  <input type="tel" name="phone" id="invitePhone" value={inviteData.phone} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-21310" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-21311">
                <label htmlFor="inviteRole" className="block text-sm font-medium text-gray-700" data-id="element-21312">
                  Role
                </label>
                <div className="mt-1" data-id="element-21313">
                  <input type="text" name="role" id="inviteRole" value={inviteData.role} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-21314" />
                </div>
              </div>
              <div className="sm:col-span-6" data-id="element-21315">
                <label htmlFor="inviteMessage" className="block text-sm font-medium text-gray-700" data-id="element-21316">
                  Invitation Message
                </label>
                <div className="mt-1" data-id="element-21317">
                  <textarea id="inviteMessage" name="message" rows={4} value={inviteData.message} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-21318" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end" data-id="element-21319">
              <button type="button" onClick={() => setCurrentView('vendors')} className="mr-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21320">
                Cancel
              </button>
              <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21321">
                <UserPlusIcon className="h-4 w-4 mr-2" data-id="element-21322" />
                Send Invitation
              </button>
            </div>
          </div>
        </div>}
    </div>;
}